import * as Y from 'yjs';

import { Logger } from '@gitbook/util-debug';
import { assertNever } from '@gitbook/util-ts';

import * as protocol from './protocol';

const logger = Logger('datasync:storage');

let warningDecodingFailedLogged = false;

/**
 * Decode an update stored in a storage.
 */
export function decodeStoredUpdate(input: Uint8Array): {
    update: Uint8Array;
    version: protocol.UpdateFormatVersion;
} {
    try {
        const message = protocol.StoredUpdate.decode(input);
        const object = protocol.StoredUpdate.toObject(message) as protocol.IStoredUpdate;
        if (!object.update || object.version === undefined || object.version === null) {
            throw new Error('StoredUpdate is missing update or version');
        }

        return {
            update: object.update,
            version: object.version,
        };
    } catch (error) {
        if (!warningDecodingFailedLogged) {
            warningDecodingFailedLogged = true;
            logger.warning(
                'Failed to decode a stored update, defaulting to V1',
                error instanceof Error ? error.message : String(error)
            );
        }
        return {
            update: input,
            version: protocol.UpdateFormatVersion.V1,
        };
    }
}

/**
 * Encode an update to be stored in a storage.
 */
export function encodeStoredUpdate(
    update: Uint8Array,
    version: protocol.UpdateFormatVersion
): Uint8Array {
    return protocol.StoredUpdate.encode({
        update,
        version,
    }).finish();
}

/**
 * Apply a stored update to a Y.Doc.
 */
export function applyStoredUpdate(doc: Y.Doc, input: Uint8Array, origin: any) {
    const decoded = decodeStoredUpdate(input);
    applyVersionedUpdate(doc, decoded.version, decoded.update, origin);
}

/**
 * Apply a versioned update to a Y.Doc.
 */
export function applyVersionedUpdate(
    doc: Y.Doc,
    version: protocol.UpdateFormatVersion | null | undefined,
    update: Uint8Array | Uint8Array[],
    origin: any
) {
    switch (version) {
        case null:
        case undefined:
        case protocol.UpdateFormatVersion.UNSPECIFIED:
        case protocol.UpdateFormatVersion.V1: {
            const mergedUpdate = Array.isArray(update) ? Y.mergeUpdates(update) : update;
            Y.applyUpdate(doc, mergedUpdate, origin);
            break;
        }
        case protocol.UpdateFormatVersion.V2: {
            const mergedUpdate = Array.isArray(update) ? Y.mergeUpdatesV2(update) : update;
            Y.applyUpdateV2(doc, mergedUpdate, origin);
            break;
        }
        default:
            assertNever(version);
    }
}
